// ============================================================
// Termin-Punkte (SPEC §50, Wanderung 044) — die kurze Liste nach einem Termin.
//
// Beschluss, Aufgabe, Info, Klärung: an einem Termin festgehalten, im Reiter
// „Termine" unter dem Termin aufgeklappt (§50.9). Was offen ist, steht danach
// OHNE zweite Pflege im Cockpit (Offen, Beschlüsse, Verlauf), im Kalender-Abo
// und in der Post der verantwortlichen Person; überfällige Klärungen kommen in
// die nächste Runde des federführenden Büros (§50.4).
//
// Eine eigene Datei neben `hooks.ts` wie `cockpit.ts`, `vorschau.ts`: `hooks.ts`
// ist die meistgeänderte Datei des Frontends. Die Regeln aus ihrem Kopf gelten
// hier genauso — Serverdaten gehören TanStack Query, Pfade ohne `/api`.
//
// ---- Zwei Festlegungen --------------------------------------------------------
//
// 1. DIE LISTE LIEGT UNTER `["projekt", projektId, …]`. Die Tabelle meldet sich
//    im Ereignisstrom zeilenweise MIT Projektkennung (044, `lp_benachrichtigen`),
//    und `lib/ereignisse.ts` entwertet bei jeder Meldung mit Kennung den Präfix
//    `["projekt", id]` — derselbe Präfix trifft die Akte (sie trägt
//    `Termin.punkte_zahl`) und jede geladene Punkteliste. Ebenso `entwerten(qc, id)`.
//
// 2. JEDER SCHREIBWEG ENTWERTET DREI PRÄFIXE (`punkteEntwerten`): die Akte samt
//    Listen (`["projekt", id]`), die Terminlisten der Zeitleiste (`["termine"]`,
//    `punkte_zahl`) und das Cockpit des Projekts (`["cockpit", id]`: Offen,
//    Beschlüsse, Verlauf). Der Ereignisstrom täte es auch — aber erst nach 500 ms,
//    und gar nicht, wenn er hängt. Der Haken (`useTerminPunktErledigt`) dazu die
//    Runden: eine abgehakte Klärung nimmt ihre Zeilen der Runde mit (§50.7 a).
//
// Hausregel (§38.11): `null` aus dem Netz kann als `undefined` ankommen —
// `verantwortlich`, `faellig`, `wartet_auf`, `erledigt_am`, `vorgaenger`,
// `nachfolger` immer mit `== null` prüfen.
// ============================================================
import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";
import { api } from "./client";
import { schluessel } from "./hooks";
import type {
  BeschlussErsetzen,
  TerminPunkt,
  TerminPunktAendern,
  TerminPunktErledigt,
  TerminPunktNeu,
} from "./types";

/* ========================================================== Schlüssel */

export const terminPunkteSchluessel = {
  /** Die Punkte EINES Termins — bewusst UNTER `["projekt", projektId]` (Festlegung 1). */
  liste: (projektId: string, terminId: string) =>
    [...schluessel.projekt(projektId), "termin", terminId, "punkte"] as const,
};

/* ========================================================== Zahlen */

/** Höchstens so viele Zeichen trägt ein Punkt (CHECK `termin_punkt_text_check`); der Server kürzt. */
export const TERMIN_PUNKT_TEXT_MAX = 500;
/** Höchstens so viele Punkte trägt ein Termin — darüber 409 (§50.5). */
export const TERMIN_PUNKTE_MAX = 100;
/** Das Ziel eines Termins (`Termin.ziel`, CHECK `termin_ziel_check`); der Server kürzt. */
export const TERMIN_ZIEL_MAX = 200;
/** §50.1: die Frist-Vorgabe einer Klärung in Tagen ab dem Berliner Tag ihres Termins. Kommt eine
 *  Klärung OHNE `faellig`, setzt der Server Termin-Tag + so viele Tage — sonst würde die schnell
 *  erfasste Klärung nie überfällig und stünde weder mit Sanduhr im Cockpit noch in der Runde.
 *  Die Oberfläche wählt dasselbe Datum bei „Bis" vor (änderbar; `null` = ausdrücklich ohne Frist).
 *  Gleich in `routers/terminpunkte.py` (`KLAERUNG_FRIST_TAGE`, test_vertrag vergleicht). */
export const KLAERUNG_FRIST_TAGE = 14;
/** Der Parameter, mit dem ein Verweis einen Termin im Reiter Termine aufklappt:
 *  `/projekte/<id>?reiter=termine&termin=<termin_id>` (Kalender, Cockpit, Verlauf — §50.9). */
export const TERMIN_PARAM = "termin";

/** Wie lange eine Liste als frisch gilt. Der Ereignisstrom entwertet ohnehin. */
const FRISCH_MS = 30_000;

/** Nach jedem Schreibweg (Festlegung 2). */
export function punkteEntwerten(qc: QueryClient, projektId: string): void {
  void qc.invalidateQueries({ queryKey: schluessel.projekt(projektId) });
  void qc.invalidateQueries({ queryKey: schluessel.termineAlle });
  void qc.invalidateQueries({ queryKey: schluessel.cockpit(projektId) });
}

/* ========================================================== Lesen */

/**
 * `GET /termine/{id}/punkte` → `TerminPunkt[]` (nach `sortierung`, `angelegt_am`, `id`).
 * `aktiv` erst, wenn der Termin aufgeklappt ist — die Zahlen stehen schon in
 * `Termin.punkte_zahl`. 404, wenn es den Termin für diese Person nicht gibt.
 */
export function useTerminPunkte(
  projektId: string | null | undefined,
  terminId: string | null | undefined,
  aktiv = true,
): UseQueryResult<TerminPunkt[]> {
  return useQuery({
    queryKey: terminPunkteSchluessel.liste(projektId ?? "", terminId ?? ""),
    queryFn: () => api.get<TerminPunkt[]>(`/termine/${terminId}/punkte`),
    enabled: !!projektId && !!terminId && aktiv,
    staleTime: FRISCH_MS,
  });
}

/* ========================================================== Schreiben */

/** `POST /termine/{id}/punkte` → 201 `TerminPunkt`. Recht: das Team des Projekts (§50.5). */
export function useTerminPunktNeu(
  projektId: string,
  terminId: string,
): UseMutationResult<TerminPunkt, Error, TerminPunktNeu> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (b: TerminPunktNeu) => api.post<TerminPunkt>(`/termine/${terminId}/punkte`, b),
    onSuccess: () => punkteEntwerten(qc, projektId),
  });
}

/** `PATCH /termine/punkte/{id}` → `TerminPunkt`. */
export function useTerminPunktAendern(
  projektId: string,
): UseMutationResult<TerminPunkt, Error, TerminPunktAendern & { id: string }> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...b }: TerminPunktAendern & { id: string }) =>
      api.patch<TerminPunkt>(`/termine/punkte/${id}`, b satisfies TerminPunktAendern),
    onSuccess: () => punkteEntwerten(qc, projektId),
  });
}

/** `DELETE /termine/punkte/{id}` → 204. Recht: wer ihn angelegt hat, Projektleitung, GF/Admin. */
export function useTerminPunktLoeschen(projektId: string): UseMutationResult<void, Error, string> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.del<void>(`/termine/punkte/${id}`),
    onSuccess: () => punkteEntwerten(qc, projektId),
  });
}

/** Was `useTerminPunktErledigt` braucht: die Kennung und die Richtung (`false` = „Rückgängig"). */
export interface TerminPunktHaken extends TerminPunktErledigt {
  id: string;
}

/**
 * `POST /termine/punkte/{id}/erledigt` `{erledigt}` → `TerminPunkt`. Recht: Team, Projektleitung,
 * GF/Admin — und wer verantwortlich ist (§50.5). Nach JEDEM Ausgang entwertet: nach einem Fehler
 * stimmt die Liste vielleicht nicht mehr.
 *
 * Zusätzlich ALLE Runden (`["runden"]`): der Haken einer Klärung erledigt in derselben Transaktion
 * die Zeilen der Runde, die ihretwegen dastehen (§50.3 `/erledigt`, §50.7 a) — `/runde` und die
 * Rundenkarte der Startseite zeigen sie sonst noch offen. Wie `useOffenAbhaken`.
 */
export function useTerminPunktErledigt(projektId: string): UseMutationResult<TerminPunkt, Error, TerminPunktHaken> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, erledigt }: TerminPunktHaken) =>
      api.post<TerminPunkt>(`/termine/punkte/${id}/erledigt`, { erledigt } satisfies TerminPunktErledigt),
    onSettled: () => {
      punkteEntwerten(qc, projektId);
      void qc.invalidateQueries({ queryKey: schluessel.rundenAlle });
    },
  });
}

/**
 * `POST /termine/punkte/{id}/ersetzen` `{text, termin_id?}` → 201 `TerminPunkt` (der NEUE Beschluss).
 * Der alte bleibt stehen und trägt danach `nachfolger` — ersetzen statt löschen (§50.6).
 */
export function useBeschlussErsetzen(
  projektId: string,
): UseMutationResult<TerminPunkt, Error, BeschlussErsetzen & { id: string }> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...b }: BeschlussErsetzen & { id: string }) =>
      api.post<TerminPunkt>(`/termine/punkte/${id}/ersetzen`, b satisfies BeschlussErsetzen),
    onSuccess: () => punkteEntwerten(qc, projektId),
  });
}
